import { useEffect, useState } from 'preact/hooks';
import { useNotice } from '../shell/notice';
import { Popover } from '../ui/Popover';
import { Chip } from '../ui/bits';
import { Segmented } from '../ui/Segmented';
import { Sheet } from '../book/reading';
import { todayIn } from '../book/model';
import type { Copy } from '../book/types';
import type { Room } from './owned';
import { loadLentNames, loadRooms } from './ownedLoad';
import { gotBack, lendCopy, placeCopies, saveCopyId, stillHave } from './actions';
import { storeIdIn } from '../queue/lookups';
import './my-books.css';

// Owned copies' own pieces (Phase 6.4 Part 3), shared by My books › Owned copies and the book
// page's paper copy cards: the room picker (Where ▾), the Lend it… sheet, and the card's tools.

/** Where a copy is: one of your rooms (or a new one), and your own words for the shelf. */
export function PlacePicker({
  rooms,
  room,
  shelf,
  several,
  roomsOnly,
  onSave,
  onCancel,
}: {
  rooms: Room[];
  room: string | null;
  shelf: string | null;
  several?: number;
  /** Rooms only (Owned copies' rows, Kristi Nov 2): Bookcase is added on the book page's copy card; the words already there are kept. */
  roomsOnly?: boolean;
  onSave: (room: string | null, shelf: string | null) => void;
  onCancel: () => void;
}) {
  const [pick, setPick] = useState<string | null>(room);
  const [fresh, setFresh] = useState('');
  const [words, setWords] = useState(shelf ?? '');
  const named = fresh.trim() || pick;
  return (
    <div class="oc-place">
      <div class="oc-label">Room</div>
      <div class="oc-place-rooms">
        {[...rooms]
          .sort((a, b) => a.order - b.order || a.name.localeCompare(b.name))
          .map((r) => (
            <Chip
              key={r.id}
              small
              label={r.name}
              on={!fresh.trim() && pick === r.name}
              onToggle={() => {
                setPick(r.name);
                setFresh('');
              }}
            />
          ))}
        <Chip small label="No room" on={!fresh.trim() && !pick} onToggle={() => setPick(null)} />
      </div>
      <label class="bk-field">
        <span class="bk-label">Or a new room</span>
        <input type="text" value={fresh} maxLength={100} placeholder="Bedroom, Boxed…" onInput={(e) => setFresh((e.target as HTMLInputElement).value)} />
      </label>
      {roomsOnly ? null : (
        <label class="bk-field">
          {/* "Bookcase", so "shelf" only ever means a reading shelf (check-up, Kristi Nov 2; n106). */}
          <span class="bk-label">Bookcase</span>
          <input type="text" value={words} maxLength={200} placeholder="Optional · e.g. Bookcase 2, top shelf" onInput={(e) => setWords((e.target as HTMLInputElement).value)} />
        </label>
      )}
      <div class="bk-buttons">
        <button type="button" class="btn-main" onClick={() => onSave(named ?? null, words.trim() || null)}>
          {several ? `Put ${several === 1 ? 'it' : `all ${several}`} ${named ? `in ${named}` : 'in no room'}` : named ? `Put it in ${named}` : 'Save: no room'}
        </button>
        <button type="button" class="btn-secondary" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/**
 * The paste box behind Owned copies' dashed "No Kindle ID ▾" / "No Audible ID ▾" (filters review,
 * Kristi Nov 2): a store link or the 10-letter ID, Save, and a search in the store (↗, it leaves the
 * app). The same words as Update details' No Kindle ID list.
 */
export function IdPaste({ copy, field, author, onDone }: { copy: { id: string; title: string }; field: 'asin' | 'audible_id'; author: string; onDone: () => void }) {
  const notice = useNotice();
  const [text, setText] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const kind = field === 'asin' ? 'Kindle' : 'Audible';
  const q = encodeURIComponent(`${copy.title} ${author}`.trim());
  const search = field === 'asin' ? `https://www.amazon.com/s?k=${q}&i=digital-text` : `https://www.audible.com/search?keywords=${q}`;
  const save = async () => {
    const id = storeIdIn(text);
    if (!id) return setErr('That isn’t a store link or a 10-letter ID.');
    setBusy(true);
    const e = await saveCopyId(notice, copy, field, id);
    setBusy(false);
    if (e) setErr(e);
    else onDone();
  };
  return (
    <>
      <label class="bk-field">
        <span class="bk-label">Paste the {kind} link or ID</span>
        <input type="text" value={text} placeholder={field === 'asin' ? 'amazon.com/dp/… or B0…' : 'audible.com/pd/… or B0…'} onInput={(e) => (setText((e.target as HTMLInputElement).value), setErr(null))} />
      </label>
      {err ? <span class="heads-up oc-id-err">{err}</span> : null}
      <div class="bk-buttons">
        <button type="button" class="btn-main" disabled={busy} onClick={() => void save()}>
          Save
        </button>
        <a class="btn-book" href={search} target="_blank" rel="noopener noreferrer">
          Search {field === 'asin' ? 'Amazon' : 'Audible'} ↗
        </a>
      </div>
    </>
  );
}

/** Lend it… (board 8; Kristi, Oct 31: store who and when): who has it, and since when. */
export function LendSheet({
  copy,
  sub,
  names: given,
  today,
  onClose,
}: {
  copy: { id: string; title: string };
  /** "Paper · Studio · Bookcase 2". */
  sub: string;
  /** Names you've lent to before; read here when not given (the book page). */
  names?: string[];
  today: string;
  onClose: () => void;
}) {
  const notice = useNotice();
  const [who, setWho] = useState('');
  const [when, setWhen] = useState<'today' | 'other'>('today');
  const [date, setDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [names, setNames] = useState<string[]>(given ?? []);
  useEffect(() => {
    if (given) return;
    void loadLentNames()
      .then(setNames)
      .catch(() => null);
  }, []);
  const save = async () => {
    setBusy(true);
    const ok = await lendCopy(notice, copy, who, when === 'today' ? today : date);
    setBusy(false);
    if (ok) onClose();
  };
  return (
    <Sheet title={`Lend ${copy.title}?`} sub={sub} onClose={onClose}>
      <label class="bk-field">
        <span class="bk-label">Who has it</span>
        <input type="text" value={who} maxLength={200} placeholder="A name" onInput={(e) => setWho((e.target as HTMLInputElement).value)} />
      </label>
      {names.length ? (
        <div class="oc-names" role="group" aria-label="Names you’ve lent to">
          {names.map((n) => (
            <Chip key={n} small plain label={n} on={who.trim().toLowerCase() === n.toLowerCase()} onToggle={() => setWho(n)} />
          ))}
        </div>
      ) : null}
      <div class="bk-field">
        <span class="bk-label">When</span>
        <Segmented label="When" options={[['today', 'Today'], ['other', 'Another day…']]} value={when} onChange={setWhen} />
        {when === 'other' ? <input type="date" aria-label="The day you lent it" value={date} max={today} onInput={(e) => setDate((e.target as HTMLInputElement).value)} /> : null}
      </div>
      <p class="bk-dialog-sub">Only you see who has it.</p>
      <div class="bk-buttons">
        <button type="button" class="btn-main" disabled={busy || !who.trim() || (when === 'other' && !date)} onClick={() => void save()}>
          Lend it
        </button>
        <button type="button" class="btn-secondary" onClick={onClose}>
          Cancel
        </button>
      </div>
    </Sheet>
  );
}

/** A paper copy card's tools on the book page: Where ▾, then Lend it… · Got it back · I still have it. */
export function CopyTools({ copy, title }: { copy: Copy; title: string }) {
  const notice = useNotice();
  const [placing, setPlacing] = useState(false);
  const [lending, setLending] = useState(false);
  const [rooms, setRooms] = useState<Room[] | null>(null);
  useEffect(() => {
    if (!placing || rooms) return;
    void loadRooms()
      .then(setRooms)
      .catch(() => setRooms([]));
  }, [placing]);
  if (copy.how === 'library_loan' || copy.format !== 'paper') return null;
  const c = { id: copy.id, title };
  const where = [copy.room, copy.shelf].filter(Boolean).join(' · ');
  const isGone = copy.state === 'lost' || copy.state === 'donated';
  return (
    <div class="bk-buttons oc-copy-tools">
      {/* A lost or donated copy keeps its room for I still have it, but isn't asked where it is. */}
      {isGone ? null : <span class="oc-where-cell">
        <button type="button" class="btn-secondary pill-menu" aria-haspopup="dialog" aria-expanded={placing} onClick={() => setPlacing(!placing)}>
          {/* A drop-down names its verb, as a Secondary pill (consistency pass, R4). */}
          {where ? 'Move it ▾' : 'Where is it? ▾'}
        </button>
        <Popover open={placing} onClose={() => setPlacing(false)} label={`Where ${title} is`} sheetTitle={`Where is ${title}?`}>
          {rooms ? (
            <PlacePicker
              rooms={rooms}
              room={copy.room ?? null}
              shelf={copy.shelf ?? null}
              onCancel={() => setPlacing(false)}
              onSave={(room, shelf) =>
                void placeCopies(notice, [copy.id], room, shelf, title).then((ok) => {
                  if (ok) setPlacing(false);
                })
              }
            />
          ) : (
            <p class="shelves-quiet">Loading your rooms…</p>
          )}
        </Popover>
      </span>}
      {copy.state === 'lent_out' ? (
        <button type="button" class="btn-book" onClick={() => void gotBack(notice, c)}>
          Got it back
        </button>
      ) : copy.state === 'lost' || copy.state === 'donated' ? (
        <button type="button" class="btn-book" onClick={() => void stillHave(notice, c)}>
          I still have it
        </button>
      ) : (
        <button type="button" class="btn-book" onClick={() => setLending(true)}>
          Lend it…
        </button>
      )}
      {lending ? <LendSheet copy={c} sub={['Paper', where].filter(Boolean).join(' · ')} today={todayIn()} onClose={() => setLending(false)} /> : null}
    </div>
  );
}

